import { Button, Header, Modal, Text } from '@bbl-digital/snorre'
import { Formik } from 'formik'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IEmailMessage } from 'shared/models/Message'
import {
  SEND_EMAIL_CREATE_DRAFT_REQUESTED,
  SEND_EMAIL_DELETE_DRAFT_REQUESTED,
  SEND_EMAIL_IS_COLLAPSED,
  SEND_EMAIL_RESET,
} from 'shared/store/sendMessage/email/actionTypes'
import {
  getIsEmailCollapsed,
  getSendEmail,
} from 'shared/store/sendMessage/email/selectors'
import { ActionButtons, ModalWrapper, Wrapper } from './styles'
import * as Yup from 'yup'
import { NoValidation, RequiredValidation } from 'shared/utils/validations'
import { getFullSignature } from 'Modules/Communication/CommunicationSettings/shared/utils/signature-helpers'
import { EMAILS_FETCH_NEW_SILENTLY_REQUESTED } from 'shared/store/communication/emails/actionTypes'
import { getEmailSignature } from 'shared/store/emailSettings/signature/selectors'
import { getEmailModalTitle } from 'shared/utils/message'
import CollapsedMessage from '../CollapsedMessage'
import { newEmailPrefix } from 'shared/constants/email'
import SendEmailForm from './SendEmailForm'
import { isMobileSafari } from 'react-device-detect'
import { useIsMobile } from '@bbl-digital/snorre-v2'

interface IProps {
  isModal?: boolean
  onSend: (message: IEmailMessage, id: string) => void
  onClose: () => void
}

const validationSchema = Yup.object().shape({
  title: RequiredValidation,
  contentHtml: NoValidation,
})

const SendEmail: FC<IProps> = ({ onClose, onSend }) => {
  const { data: signature } = useSelector(getEmailSignature)
  const isCollapsed = useSelector(getIsEmailCollapsed)
  const isMobile = useIsMobile()
  const { message, messageId, isDraftUpdated, isEditDraft } =
    useSelector(getSendEmail)
  const [showSaveDraft, setShowSaveDraft] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(isMobile)
  const dispatch = useDispatch()

  const defaultContent = signature
    ? `${newEmailPrefix}${getFullSignature(
        signature?.standardLines,
        signature?.additionalLines
      )}`
    : ''

  useEffect(() => {
    if (messageId) return

    dispatch({
      type: SEND_EMAIL_CREATE_DRAFT_REQUESTED,
      payload: defaultContent,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCollapsed = (value: boolean) => {
    dispatch({
      type: SEND_EMAIL_IS_COLLAPSED,
      payload: value,
    })
  }

  const handleClose = (withDelete?: boolean) => (e?: React.MouseEvent) => {
    // iOS doesnt handle blur.
    // So we need to focus on the button that is being clicked.
    if (isMobileSafari && e?.currentTarget) {
      const event = e as React.MouseEvent<HTMLButtonElement>
      event.currentTarget.focus()
    }

    if (withDelete && messageId) {
      deleteDraft()
    } else {
      dispatch({
        type: EMAILS_FETCH_NEW_SILENTLY_REQUESTED,
        payload: { isOnEmailPage: true },
      })
    }

    dispatch({ type: SEND_EMAIL_RESET })
    onClose()
  }

  const deleteDraft = () => {
    dispatch({
      type: SEND_EMAIL_DELETE_DRAFT_REQUESTED,
      payload: messageId,
    })
  }

  const handleCloseClicked = () => {
    if (isEditDraft) {
      handleClose(false)()
      return
    }

    if (isDraftUpdated) {
      setShowSaveDraft(true)
      return
    }

    handleClose(true)()
  }

  return (
    <Formik<IEmailMessage>
      validationSchema={validationSchema}
      onSubmit={(e) => onSend(e, messageId || '')}
      initialValues={{
        title: message?.title || '',
        contentHtml: message?.contentHtml || defaultContent,
      }}
    >
      {({ values }) => {
        if (showSaveDraft) {
          return (
            <Modal
              open
              header={<Header level={4}>Lagre utkast</Header>}
              width="450px"
              onClose={() => setShowSaveDraft(false)}
            >
              <Text>Vil du lagre utkastet?</Text>
              <ActionButtons $includeMarginTop>
                <Button outline onClick={handleClose(true)}>
                  Nei
                </Button>
                <Button onClick={handleClose()}>Ja</Button>
              </ActionButtons>
            </Modal>
          )
        }

        return (
          <>
            {isCollapsed && (
              <CollapsedMessage
                title={values.title}
                onClose={() => handleCloseClicked()}
                onOpen={() => handleCollapsed(false)}
              />
            )}

            <ModalWrapper collapsed={isCollapsed}>
              <Modal
                open
                disableFocusTrap={isCollapsed}
                disableRefFocus
                toggleFullscreen
                defaultFullscreen={isMobile}
                disableLocalClose
                disableScroll={!isCollapsed}
                onCollapse={() => handleCollapsed(true)}
                width="1100px"
                onClose={handleCloseClicked}
                onFullscreenToggle={setIsFullscreen}
                header={
                  <Header level={4}>
                    {getEmailModalTitle(
                      message?.parentRelation?.relation,
                      message?.title
                    )}
                  </Header>
                }
              >
                <Wrapper>
                  <SendEmailForm
                    isFullscreen={isFullscreen}
                    handleClose={handleClose()}
                  />
                </Wrapper>
              </Modal>
            </ModalWrapper>
          </>
        )
      }}
    </Formik>
  )
}

export default SendEmail
